import {storage} from "utils";
import {toggleBodyTheme} from "utils/toggle-body-theme";
import {
  ThemeMode,
  IThemeContext,
  ReducerThemeState,
  ReducerThemeAction,
} from "interfaces";
import {
  JSX,
  useMemo,
  useEffect,
  useContext,
  useReducer,
  useCallback,
  createContext,
} from "react";

const ThemeContext = createContext<IThemeContext>({
  mode: "light",
  isDark: false,
  toggle: () => undefined,
});

export const useContextTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({
  children,
}: Readonly<{children: JSX.Element | JSX.Element[]}>) => {
  const {read, set} = storage;
  const initialMode = read<ThemeMode>("[theme]") || "light";
  const [state, dispatch] = useReducer(
    (
      state: ReducerThemeState,
      action: ReducerThemeAction
    ): ReducerThemeState => {
      switch (action.type) {
        case "toggle":
          const newMode =
            action["payload"] ?? state.mode === "dark" ? "light" : "dark";
          set("[theme]", newMode);
          return {
            ...state,
            mode: newMode,
          };

        default:
          return state;
      }
    },
    {mode: initialMode, isDark: initialMode === "dark"}
  );

  const toggle = useCallback((payload?: ThemeMode) => {
    dispatch({type: "toggle", payload});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    toggleBodyTheme(state["mode"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const value = useMemo(
    () => ({...state, isDark: state.mode === "dark", toggle}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
